import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  ContentLayout, 
  Header, 
  SpaceBetween, 
  Container
} from "@cloudscape-design/components";
import Button from "../common/Button";
import { ButtonDropdown } from "../common/ButtonDropdown";
import type { DropdownOption } from "../common/ButtonDropdown";
import SectionHeader from "../common/SectionHeader";
import { LoadingScreen, ErrorScreen } from "../common/UIStates";
import { InfoSection, FiltersDisplay, BadgeSection, PlaceholderSection } from "../common/ReportComponents";
import { RecentActivityItem } from "../Dashboard/RecentActivityTable/types";
import { ActivityItem } from "../AllActivities/types";
import { MOCK_REPORT_FILTERS } from "src/constants/reportConstants";
import { useWorkflowStore } from "../../store/workflow-store";
import { useGlobalStore } from "../../store/global-store";
import { 
  shouldEnableDownload, 
  generateDownloadOptions, 
  downloadFile
} from "../../utils/workflowExecutionUtils";
import { getStatusBadgeColor } from "../../utils/statusUtils";
import { BadgeColor } from "../../constants/reportConstants";
import "../AllReportsAndAnalysis/components/create-dropdown-button.scss";

interface ReportViewPageProps {
  reportData?: ActivityItem;
  loading?: boolean;
}

const ReportViewPage: React.FC<ReportViewPageProps> = ({
  reportData,
  loading = false,
}) => {
  const navigate = useNavigate();
  const { selectedSpaceId } = useGlobalStore();
  
  // TODO: Until the report generation integration is complete, we will use mocked request to invoke the API.
  // Once the report generation logic is complete, we will use the actual values for the input request.
  const mockRequest = {
    spaceId: "0b2d6f38-d273-420d-a464-38a397ddb56b",
    workflowId: "cf75ecef-5a3a-45a2-8165-5b11dbe1b2eb", 
    workflowExecutionId: "822d72cd-33ef-4b5d-a853-8c8154f874a2"
  };
  
  const { workflowExecution, isExecutionLoading, error: executionError, getWorkflowExecution } = useWorkflowStore();
  
  // Aliases for backward compatibility
  const executionData = workflowExecution;
  const executionLoading = isExecutionLoading;
  
  useEffect(() => {
    getWorkflowExecution(mockRequest);
  }, [getWorkflowExecution]);

  // Handle download based on API response
  const handleDownload = async (option: DropdownOption) => {
    const downloadOption = downloadOptions.find(opt => opt.label === option.label);
    if (downloadOption) {
      try {
        await downloadFile(downloadOption.s3URI);
      } catch (error) {
        console.error('Download failed:', error);
        
        return (
          <ErrorScreen
            title="Failed to download report"
            message="Error while downloading report. Please try again later."
            onButtonClick={handleBack}
          />
        );
      }
    }
  };

  // Get download options from API response
  const downloadOptions = generateDownloadOptions(executionData?.result);
  const isDownloadDisabled = !shouldEnableDownload(executionData?.result);

  const handleBack = () => {
    navigate(-1);
  };

  // Show loading state for both report data and execution data
  if (loading || executionLoading) {
    return <LoadingScreen message="Loading report..." />;
  }

  // Show error state for execution API
  if (executionError) {
    return (
      <ErrorScreen
        title="Failed to fetch report"
        message="Unable to load report details. Please try again later."
        onButtonClick={handleBack}
      />
    );
  }

  // Error state for report data
  if (!reportData) {
    return (
      <ErrorScreen
        title="Report not found"
        message="The requested report could not be found"
        onButtonClick={handleBack}
      />
    );
  }

  // TODO: Replace with actual API data - these are mock configuration details
  const mockConfigurationData = {
    filters: MOCK_REPORT_FILTERS,
    reportSections: [
      "Executive Summary",
      "P&L by Channel", 
      "P&L by Region",
      "Cost Analysis",
      "Variance Analysis"
    ]
  };

  const reportInfoItems = [
    { type: "badge" as const, content: reportData.category, badgeVariant: "category" as const, badgeColor: BadgeColor.BLUE },
    // Show workflow execution status beside report type
    { type: "badge" as const, content: executionData?.result?.status || "Unknown", badgeVariant: "status" as const, badgeColor: getStatusBadgeColor(executionData?.result?.status) },
    { type: "text" as const, content: reportData.description },
    { type: "text" as const, content: `Generated: ${reportData.date}` }
  ];

  return (
    <ContentLayout
      defaultPadding
      header={
        <Header
          variant="h1"
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="secondary" onClick={handleBack}>
                Back
              </Button>
              <ButtonDropdown
                variant="primary"
                options={downloadOptions.map((option, index) => ({
                  id: `download-${index}`,
                  label: option.label
                }))}
                onItemClick={handleDownload}
                disabled={isDownloadDisabled}
                className="create-dropdown-wrapper"
              >
                Download
              </ButtonDropdown>
            </SpaceBetween>
          }
        >
          {reportData.reportName}
        </Header>
      }
    >
      <SpaceBetween direction="vertical" size="l">
        {/* Report Info Section */}
        <InfoSection items={reportInfoItems} />

        {/* Configuration Details Section */}
        <div>
          <SectionHeader title="Configuration Details" variant="h2" />
          <Container>
            <SpaceBetween direction="vertical" size="m">
              <FiltersDisplay filters={mockConfigurationData.filters} />
              <BadgeSection title="Report Sections" items={mockConfigurationData.reportSections} />
              <div style={{ color: "var(--color-text-status-info)", fontSize: "var(--font-size-body-s)" }}>
                TODO: Configuration data will be fetched dynamically from API
              </div>
            </SpaceBetween>
          </Container>
        </div>

        {/* Report Preview Section */}
        <PlaceholderSection
          title="Report Preview"
          placeholderTitle="Preview not available"
          message="TODO: Report content will be fetched from S3 and rendered dynamically"
        />
      </SpaceBetween>
    </ContentLayout>
  );
};

export default ReportViewPage;
